// @flow

import React from "react";
import "./CarModelDropdown.scss";
import {translate, withTranslation} from "react-i18next";

export type VehicleNode = {
    key: string,
    name: string,
    title: string,
    categories: ?VehicleNode[],
}

export type CarModelDropdownItemProps = {
    vehicleModels: VehicleNode[],
    vehicleSeries: VehicleNode,
    setVehicle: (string, string) => void,
    t: translate,
    isVisible: boolean,
    toggleVehicleSelector: string => void,
    carRef: ?HTMLElement,
    allowsClick: boolean,
}

export type CarModelDropdownItemState = {
    navigationPath: VehicleNode[],
    selectedNode: ?VehicleNode,
}

const MAX_SUBCATEGORY_DEPTH = 2;

class CarModelDropdownItem extends React.Component<CarModelDropdownItemProps, CarModelDropdownItemState> {
    constructor(props: CarModelDropdownItemProps) {
        super(props);
        this.elementRef = React.createRef();
        this.state = {
            navigationPath: [this.getRootNode(props)],
            selectedNode: null,
        };
    }

    getRootNode = (props: CarModelDropdownItemProps) => ({
        ...props.vehicleSeries,
        categories: props.vehicleModels || [],
    });

    getCurrentNode = () => this.state.navigationPath[this.state.navigationPath.length - 1];

    getChildren = node => (node && node.categories && this.state.navigationPath.length ? node.categories : []);

    outsideClickDetected = event => {
        if (this.elementRef && !this.elementRef.current.contains(event.target) &&
            this.props.carRef && !this.props.carRef.current.contains(event.target)) {
            this.props.toggleVehicleSelector("");
        }
    };

    alignDropdown = () => {
        const thisDiv = document.getElementById("hpm-carModelDropdownId");
        if (!thisDiv) return;
        const dropdownRect = thisDiv.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const parentRect = thisDiv.offsetParent ? thisDiv.offsetParent.getBoundingClientRect() : dropdownRect;
        const parentCenterX = parentRect.left + parentRect.width / 2;
        const dropdownHalfWidth = dropdownRect.width / 2;

        let leftOffset = parentCenterX - dropdownHalfWidth;
        let rightOffset = parentCenterX + dropdownHalfWidth;

        if (leftOffset < 0) {
            // Dropdown würde links abgeschnitten - links ausrichten
            thisDiv.style.left = "0";
            thisDiv.style.transform = "translateX(0)";
        } else if (rightOffset > viewportWidth) {
            // Dropdown würde rechts abgeschnitten - rechts ausrichten
            thisDiv.style.left = "auto";
            thisDiv.style.right = "0";
            thisDiv.style.transform = "translateX(0)";
        } else {
            // Zentriert
            thisDiv.style.left = "50%";
            thisDiv.style.transform = "translateX(-50%)";
        }
    };

    componentDidMount() {
        document.addEventListener("mousedown", this.outsideClickDetected);
        this.alignDropdown();
    }

    componentDidUpdate(prevProps: CarModelDropdownItemProps) {
        if (prevProps.vehicleSeries.key !== this.props.vehicleSeries.key ||
            prevProps.vehicleModels !== this.props.vehicleModels) {
            this.setState({
                navigationPath: [this.getRootNode(this.props)],
                selectedNode: null,
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.outsideClickDetected);
    }

    handleBackClick = () => {
        if (!this.props.allowsClick) return;
        this.setState(prevState => {
            if (prevState.navigationPath.length < 2) return null;
            return {
                navigationPath: prevState.navigationPath.slice(0, -1),
                selectedNode: null,
            };
        });
    };

    selectNode = node => {
        const isRootSelection = node.key === this.props.vehicleSeries.key;
        this.props.setVehicle(
            isRootSelection ? this.props.vehicleSeries.key : null,
            isRootSelection ? null : node.key,
        );
    };

    collapseSingleChildChain = node => {
        let current = node;
        const visitedKeys = new Set();
        while (current && !visitedKeys.has(current.key)) {
            visitedKeys.add(current.key);
            const children = this.getChildren(current);
            if (children.length !== 1) break;
            current = children[0];
        }
        return current;
    };

    handleDoneClick = () => {
        if (!this.props.allowsClick) return;
        const currentNode = this.getCurrentNode();
        const selection = this.state.selectedNode || currentNode;
        if (!selection) return;
        this.selectNode(selection);
    };

    handleNodeClick = node => {
        if (!this.props.allowsClick) return;
        const target = this.collapseSingleChildChain(node);
        const children = this.getChildren(target);
        const canNavigateDeeper = this.state.navigationPath.length < MAX_SUBCATEGORY_DEPTH;
        if (children.length > 1 && canNavigateDeeper) {
            this.setState(prevState => ({
                navigationPath: [...prevState.navigationPath, target],
                selectedNode: null,
            }));
            return;
        }
        this.selectNode(target);
    };

    getBreadcrumb = () => {
        const currentNode = this.getCurrentNode();
        const selectedNode = this.state.selectedNode;
        const items = this.state.navigationPath
            .map(node => node.title || node.name)
            .filter(Boolean);

        if (selectedNode && selectedNode.key !== currentNode.key) {
            items.push(selectedNode.title || selectedNode.name);
        }

        return items.join(" ➜ ");
    };

    render() {
        const currentNode = this.getCurrentNode();
        const children = this.getChildren(currentNode);
        const selectedNode = this.state.selectedNode;
        const breadcrumb = this.getBreadcrumb();
        const canNavigateDeeper = this.state.navigationPath.length < MAX_SUBCATEGORY_DEPTH;

        return <div className={"hpm-carModelDropdown"} ref={this.elementRef}
                    id={"hpm-carModelDropdownId"}
                    tabIndex={-1}
        >
            <span className={"hpm-boxStart"}/>

            {breadcrumb && (
                <div className={"hpm-carModelBreadcrumb"}>
                    {breadcrumb}
                </div>
            )}

            <button className={"hpm-carModelItem hpm-carModelItem--action"}
                    onClick={this.handleDoneClick}
                    type={"button"}
                    tabIndex={0}
            >
                 {this.props.t("navigation.select")}
            </button>

            {children.map((model: VehicleNode) => {
                const isSelected = selectedNode && selectedNode.key === model.key;
                const hasChildren = canNavigateDeeper && this.getChildren(model).length > 0;
                const itemClassName = [
                    "hpm-carModelItem",
                    hasChildren ? "hpm-carModelItem--hasChildren" : "",
                    isSelected ? "hpm-carModelItem--selected" : "",
                ].filter(Boolean).join(" ");

                return (
                    <button className={itemClassName} key={model.key}
                            onClick={() => this.handleNodeClick(model)}
                            onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                    this.handleNodeClick(model);
                                }
                            }}
                            type={"button"}
                            tabIndex={0}>
                        {model.title || model.name}
                    </button>
                );
            })}

            {this.state.navigationPath.length > 1 && (
                <button className={"hpm-carModelItem hpm-carModelItem--action"}
                        onClick={this.handleBackClick}
                        type={"button"}
                        tabIndex={0}
                >
                    {this.props.t("navigation.historyBack")}
                </button>
            )}

            <span className={"hpm-boxEnd"}/>
        </div>;
    }
}

export default withTranslation()(CarModelDropdownItem);
